import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { detectUILangSync, detectUILangAsync } from "../../utils/detectUILang";
import { Button, Input, Select, Tabs, Tooltip, Typography, message } from "antd";
import useUserContext from "../../hooks/useUserContext";
import { postAgentChat } from "../../api/v2/agent";
import {
    RegisterV2Onboarding,
    VerifyV2Onboarding,
    CreateV2OnboardingApiKey,
    StartV2OnboardingContact,
    SendV2OnboardingEmailCode,
    VerifyV2OnboardingEmailCode,
    SendV2OnboardingPhoneCode,
    VerifyV2OnboardingPhoneCode,
    DetectV2OnboardingPlatform,
} from "../../api/v2/onboarding";
import "./v2-onboarding-agent.css";
import {Navigate} from "react-router-dom";
import VuelveLogo from "../../assets/images/logo-bright.png";
import WooIcon from "../../assets/images/woo-icon.png";
import ShopifyIconPng from "../../assets/images/shopify-icon-dark-text.png";
import FlagUS from "../../assets/images/flags/us.png";
import FlagIL from "../../assets/images/flags/il.png";
import CopyToClipboardCode from "../../components/copyToClipboardCode/CopyToClipboardCode";
import CountriesSelector from "../../components/contriesSelector/CountriesSelector";
import { cleanPhoneNumberOnlyDigits } from "../../utils/cleanPhoneNumberString";
import { countryCodesList } from "../../utils/countryCodesList";
import axios from "axios";

const { Text } = Typography;

const SESSION_KEY = "v2_onboarding_session";
const SESSION_TTL_MS = 3 * 24 * 60 * 60 * 1000; // 3 days
const LANG_KEY = "v2_onboarding_lang";
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";

type UILang = "en" | "he";
const LANG_OPTIONS: { code: UILang; label: string; flagSrc: string }[] = [
    { code: "en", label: "English", flagSrc: FlagUS },
    { code: "he", label: "עברית", flagSrc: FlagIL },
];

const TR = {
    en: {
        idle_title: "Hey, how can I help you?",
        login: "Login",
        back_to_website: "Back to website",
        start_onboarding: "Start onboarding",
        back_to_onboarding: "Back to onboarding",
        new_onboarding_tooltip: "Start a new onboarding process",
        placeholder: 'Ask a question or type "start onboarding"',
        // contact
        contact_title: "Contact details",
        first_name: "First name", first_name_ph: "John",
        last_name: "Last name", last_name_ph: "Doe",
        email: "Email",
        phone: "Phone",
        phone_ph: "Phone Number",
        err_first_name: "First name is required",
        err_last_name: "Last name is required",
        err_email: "Email is required",
        err_phone: "Phone is required",
        err_phone_invalid: "Enter a valid phone number with country code",
        err_contact_save: "Failed to save contact details",
        err_email_send: "Failed to send email code",
        btn_continue_email: "Continue to email verification",
        // email
        email_title: "Verify email",
        verification_code: "Verification code",
        err_missing_token: "Missing registration token",
        err_enter_code: "Enter the code",
        err_invalid_code: "Invalid code",
        btn_resend: "Resend",
        btn_verify_email: "Verify email",
        // phone
        phone_title: "Verify phone",
        err_phone_send: "Failed to send phone code",
        btn_verify_phone: "Verify phone",
        // platform / website detection
        platform_title: "Your website",
        platform_url_label: "Website URL",
        platform_url_ph: "yourstore.com",
        platform_url_help: "Enter your store's domain so we can set up the right integration for you.",
        platform_detecting: "Detecting your platform…",
        platform_err_missing: "Please enter your website URL",
        platform_err_failed: "Could not detect your platform. Please check the URL and try again.",
        platform_btn: "Continue",
        shopify_result: "Great choice! To get started with Vuelve on your Shopify store, install the Vuelve app directly from the Shopify App Store.",
        shopify_next: "After installing, open the Vuelve app from your Shopify admin → Apps section, and continue the setup from there.",
        shopify_cta: "Install Vuelve on Shopify",
        woo_result: "Perfect! To connect Vuelve with your WooCommerce store, install the Vuelve plugin from the WordPress plugin directory.",
        woo_next_1: "After activating the plugin, go to the Vuelve page in your WordPress sidebar and create a user.",
        woo_next_2: "Then head to app.vuelve.co and log in with the email you used when creating that user.",
        woo_cta: "Install Vuelve for WooCommerce",
        standalone_result: "We're on it! Standalone website support is still a work in progress. We'll reach out to you personally once it's ready — we have your contact details and will be in touch soon.",
        // pricing
        pricing_title: "Our pricing plans",
        pricing_per_month: "/mo",
        pricing_marketing: "marketing messages/mo",
        pricing_conversation: "conversation messages/mo",
        pricing_phone_included: "US phone number included",
        pricing_non_us_note: "Prices above apply with a US phone number. Non-US numbers may incur additional charges.",
        pricing_start: "Get started",
    },
    he: {
        idle_title: "היי, כיצד אוכל לעזור?",
        login: "כניסה",
        back_to_website: "חזרה לאתר",
        start_onboarding: "התחל הרשמה",
        back_to_onboarding: "חזרה להרשמה",
        new_onboarding_tooltip: "התחל תהליך הרשמה חדש",
        placeholder: 'שאל שאלה או כתוב "התחל הרשמה"',
        // contact
        contact_title: "פרטי קשר",
        first_name: "שם פרטי", first_name_ph: "ישראל",
        last_name: "שם משפחה", last_name_ph: "ישראלי",
        email: 'דוא"ל',
        phone: "טלפון",
        phone_ph: "מספר טלפון",
        err_first_name: "שם פרטי הוא שדה חובה",
        err_last_name: "שם משפחה הוא שדה חובה",
        err_email: 'דוא"ל הוא שדה חובה',
        err_phone: "מספר טלפון הוא שדה חובה",
        err_phone_invalid: "הזן מספר טלפון תקין עם קידומת מדינה",
        err_contact_save: "שמירת פרטי הקשר נכשלה",
        err_email_send: 'שליחת קוד דוא"ל נכשלה',
        btn_continue_email: 'המשך לאימות דוא"ל',
        // email
        email_title: 'אימות דוא"ל',
        verification_code: "קוד אימות",
        err_missing_token: "חסר טוקן הרשמה",
        err_enter_code: "הזן את הקוד",
        err_invalid_code: "קוד שגוי",
        btn_resend: "שלח שוב",
        btn_verify_email: 'אמת דוא"ל',
        // phone
        phone_title: "אימות טלפון",
        err_phone_send: "שליחת קוד טלפון נכשלה",
        btn_verify_phone: "אמת טלפון",
        // platform / website detection
        platform_title: "האתר שלך",
        platform_url_label: "כתובת האתר",
        platform_url_ph: "yourstore.com",
        platform_url_help: "הזן את הדומיין של החנות שלך כדי שנוכל להגדיר עבורך את האינטגרציה הנכונה.",
        platform_detecting: "מזהה את הפלטפורמה שלך…",
        platform_err_missing: "נא להזין את כתובת האתר",
        platform_err_failed: "לא הצלחנו לזהות את הפלטפורמה. בדוק את הכתובת ונסה שוב.",
        platform_btn: "המשך",
        shopify_result: "בחירה מצוינת! כדי להתחיל עם Vuelve בחנות Shopify שלך, התקן את אפליקציית Vuelve ישירות מ-Shopify App Store.",
        shopify_next: "לאחר ההתקנה, פתח את אפליקציית Vuelve מאזור האפליקציות בניהול Shopify שלך והמשך את ההגדרות משם.",
        shopify_cta: "התקן את Vuelve על Shopify",
        woo_result: "מעולה! כדי לחבר את Vuelve לחנות WooCommerce שלך, התקן את התוסף מספריית התוספים של WordPress.",
        woo_next_1: "לאחר הפעלת התוסף, עבור לעמוד Vuelve בסרגל הצד של WordPress וצור משתמש.",
        woo_next_2: 'לאחר מכן עבור לאתר app.vuelve.co והתחבר עם הדוא"ל שהשתמשת בו ביצירת המשתמש.',
        woo_cta: "התקן את Vuelve ל-WooCommerce",
        standalone_result: "אנחנו על זה! תמיכה באתרים עצמאיים עדיין בפיתוח. ניצור איתך קשר אישי ברגע שיהיה מוכן — יש לנו את פרטי הקשר שלך.",
        // pricing
        pricing_title: "תוכניות המחיר שלנו",
        pricing_per_month: "לחודש",
        pricing_marketing: "הודעות שיווק בחודש",
        pricing_conversation: "הודעות שיחה בחודש",
        pricing_phone_included: "מספר טלפון אמריקאי כלול",
        pricing_non_us_note: "המחירים לעיל תקפים עם מספר טלפון אמריקאי. מספרים שאינם אמריקאיים עשויים לגרור חיובים נוספים.",
        pricing_start: "להתחיל",
    },
} as const;

const STAGE_STEPS: Record<string, ChatEntry["type"][]> = {
    contact:  ["contact"],
    email:    ["contact", "email"],
    phone:    ["contact", "email", "phone"],
    platform: ["contact", "email", "phone", "platform"],
    step1:    ["contact", "email", "phone", "platform", "step1"],
    step2:    ["contact", "email", "phone", "platform", "step1", "step2"],
    step3:    ["contact", "email", "phone", "platform", "step1", "step2", "keys"],
};

type Stage = "idle" | "contact" | "email" | "phone" | "platform" | "step1" | "step2" | "step3";
type PlatformChoice = "shopify" | "woocommerce" | "standalone";

type PricingPlanEntry = {
    id: number;
    title: string;
    description?: string;
    fee: number;
    freeCreditPerCycle?: number;
    freeConversationMessagesPerCycle?: number;
    isPhoneNumberIncluded: boolean;
    badge?: string;
    cta?: string;
};

type ChatEntry = {
    id: string;
    role: "assistant" | "user";
    type?: "text" | "contact" | "email" | "phone" | "platform" | "step1" | "step2" | "step3" | "keys" | "steps";
    text?: string;
    action?: "start_onboarding" | "login" | "show_pricing" | "none";
    actionLabel?: string;
    language?: string;
    isRtl?: boolean;
    pricingPlans?: PricingPlanEntry[];
};

type AgentState = {
    verify_token?: string | null;
    shop_id?: number | null;
    public_key?: string | null;
    secret_key?: string | null;
};

const SendFilledIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M21.4325,4.86103 L15.4325,20.361 C15.175,21.0261 14.324,21.2156 13.8087,20.7227 L10.4266,17.4876 L8.35348,19.5607 C8.0385,19.8757 7.49993,19.6526 7.49993,19.2072 L7.49993,14.6882 L2.30868,9.72268 C1.74196,9.1806 1.99133,8.22685 2.75086,8.03155 L20.2509,3.53155 C21.0389,3.32889 21.7262,4.10218 21.4325,4.86103 Z M19,6.00006 L8.03159,13.1534 L9.76704,14.8134 L19,6.00006 Z" />
    </svg>
);

const LoginIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M10 3a1 1 0 0 1 1 1v3h-2V5H5v14h4v-2h2v3a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h6Zm9.707 8.293a1 1 0 0 1 0 1.414l-4 4-1.414-1.414L16.586 13H9v-2h7.586l-2.293-2.293 1.414-1.414 4 4Z" />
    </svg>
);

export default function V2OnboardingAgentPage() {
    const { selectedDomain, user, isInitialLoadDone } = useUserContext();
    const apiDomain = selectedDomain || "vuelve.co";
    const [conversationId, setConversationId] = useState<string | null>(null);
    const [stage, setStage] = useState<Stage>("idle");
    const [loading, setLoading] = useState(false);
    const [isThinking, setIsThinking] = useState(false);

    const [inputText, setInputText] = useState("");
    const [messages, setMessages] = useState<ChatEntry[]>([]);
    const messagesRef = useRef<HTMLDivElement | null>(null);
    const autoScrollRef = useRef(true);
    const turnstileRef = useRef<HTMLDivElement | null>(null);
    const turnstileWidgetIdRef = useRef<string | null>(null);

    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [phoneCountry, setPhoneCountry] = useState("US");
    const [phoneLocal, setPhoneLocal] = useState("");
    const [emailCode, setEmailCode] = useState("");
    const [phoneCode, setPhoneCode] = useState("");

    const [domain, setDomain] = useState("");
    const [friendlyName, setFriendlyName] = useState("");
    const [method, setMethod] = useState<"dns" | "html" | "meta">("meta");

    const [verifyToken, setVerifyToken] = useState<string | null>(null);
    const [onboardingId, setOnboardingId] = useState<number | null>(null);
    const [onboardingToken, setOnboardingToken] = useState<string | null>(null);
    const [publicKey, setPublicKey] = useState<string | null>(null);
    const [secretKey, setSecretKey] = useState<string | null>(null);
    const [implStage, setImplStage] = useState<"keys" | "server" | "client">("keys");
    const [showWhatsNext, setShowWhatsNext] = useState(false);

    const [selectedPlatform, setSelectedPlatform] = useState<PlatformChoice | null>(null);
    const [websiteUrl, setWebsiteUrl] = useState("");
    const [isDetecting, setIsDetecting] = useState(false);
    const [platformError, setPlatformError] = useState<string | null>(null);
    const [uiLang, setUiLang] = useState<UILang>(() => {
        const stored = localStorage.getItem(LANG_KEY);
        if (stored === "he" || stored === "en") return stored;
        return detectUILangSync();
    });
    useEffect(() => {
        const stored = localStorage.getItem(LANG_KEY);
        if (stored === "he" || stored === "en") return;
        detectUILangAsync().then((lang) => setUiLang(lang));
    }, []);

    const getTurnstileToken = (): Promise<string> => {
        return new Promise((resolve) => {
            const w = window as any;
            if (!TURNSTILE_SITE_KEY || !w.turnstile || !turnstileRef.current) {
                console.log("[turnstile] not available", { TURNSTILE_SITE_KEY: !!TURNSTILE_SITE_KEY, turnstile: !!w.turnstile, ref: !!turnstileRef.current });
                resolve("");
                return;
            }
            if (turnstileWidgetIdRef.current) {
                try { w.turnstile.remove(turnstileWidgetIdRef.current); } catch {}
                turnstileWidgetIdRef.current = null;
            }
            console.log("[turnstile] rendering widget");
            const widgetId = w.turnstile.render(turnstileRef.current, {
                sitekey: TURNSTILE_SITE_KEY,
                size: "invisible",
                callback: (token: string) => {
                    console.log("[turnstile] got token", token?.slice(0, 20));
                    turnstileWidgetIdRef.current = null;
                    resolve(token);
                },
                "error-callback": () => {
                    console.log("[turnstile] error");
                    turnstileWidgetIdRef.current = null;
                    resolve("");
                },
                "expired-callback": () => {
                    console.log("[turnstile] expired");
                },
            });
            turnstileWidgetIdRef.current = widgetId;
            console.log("[turnstile] executing widget", widgetId);
            w.turnstile.execute(widgetId);
        });
    };
    const currentLangOption = LANG_OPTIONS.find((o) => o.code === uiLang) ?? LANG_OPTIONS[0];
    const tr = TR[uiLang];
    const isRtlUI = uiLang === "he";

    const [contactError, setContactError] = useState<string | null>(null);
    const [emailError, setEmailError] = useState<string | null>(null);
    const [phoneError, setPhoneError] = useState<string | null>(null);
    const [step1Error, setStep1Error] = useState<string | null>(null);
    const [step2Error, setStep2Error] = useState<string | null>(null);
    const [step3Error, setStep3Error] = useState<string | null>(null);

    const hasMessages = messages.length > 0;
    const inputDisabled = false;


    const scrollToBottom = () => {
        const spacer = document.getElementById("messages-spacer");
        spacer?.scrollIntoView({ behavior: "smooth" });
    };

    const scrollToLastOnboarding = () => {
        const steps = Array.from(document.querySelectorAll(".v2oa-step"));
        const node = steps[steps.length - 1] as HTMLElement | undefined;
        if (node?.scrollIntoView) node.scrollIntoView({ behavior: "smooth", block: "center" });
    };

    const buildE164 = (country: string, local: string) => {
        let digits = cleanPhoneNumberOnlyDigits(local || "");
        if (digits.startsWith("0")) digits = digits.slice(1);
        const code = countryCodesList.find((c) => c.country === country)?.code;
        if (!digits || !code) return "";
        return `+${code}${digits}`;
    };

    useLayoutEffect(() => {
        if (autoScrollRef.current) scrollToBottom();
    }, [messages.length, stage, isThinking]);

    useEffect(() => {
        if (!autoScrollRef.current) return;
        const t1 = window.setTimeout(scrollToBottom, 0);
        const t2 = window.setTimeout(scrollToBottom, 50);
        const t3 = window.setTimeout(scrollToBottom, 150);
        return () => {
            window.clearTimeout(t1);
            window.clearTimeout(t2);
            window.clearTimeout(t3);
        };
    }, [messages.length, stage, isThinking]);

    useEffect(() => {
        if (!isInitialLoadDone) return;

        const STORAGE_KEY = "v2_onboarding_plan_hint";
        const now = Date.now();
        const currentRaw = localStorage.getItem(STORAGE_KEY);
        if (currentRaw) {
            try {
                const current = JSON.parse(currentRaw);
                if (!current?.expiresAt || Number(current.expiresAt) <= now) {
                    localStorage.removeItem(STORAGE_KEY);
                }
            } catch {
                localStorage.removeItem(STORAGE_KEY);
            }
        }

        // Persist plan hint only for non-authenticated visitors.
        if (user) return;

        const params = new URLSearchParams(window.location.search);
        const plan = (params.get("plan") || "").trim();
        if (!plan) return;
        localStorage.setItem(
            STORAGE_KEY,
            JSON.stringify({
                value: plan,
                expiresAt: now + 24 * 60 * 60 * 1000,
            })
        );
    }, [isInitialLoadDone, user]);

    useEffect(() => {
        const setCountryFromLocation = async () => {
            if (phoneLocal || phoneCountry !== "US") return;
            try {
                const ipRes = await axios.get("https://api.ipify.org/?format=json");
                const ip = ipRes?.data?.ip;
                if (!ip) return;
                const res = await axios.get(`https://api.country.is/${ip}`);
                const countryItem = countryCodesList.find((c) => c.country === res.data.country);
                if (countryItem?.country) {
                    setPhoneCountry(countryItem.country);
                }
            } catch {
                // ignore
            }
        };
        setCountryFromLocation();
    }, [phoneCountry, phoneLocal]);

    // Auto-save session to localStorage whenever meaningful state changes
    useEffect(() => {
        if (stage === "idle") return;
        const session = {
            expiresAt: Date.now() + SESSION_TTL_MS,
            stage,
            onboardingId,
            onboardingToken,
            verifyToken,
            selectedPlatform,
            firstName,
            lastName,
            email,
            phone,
            phoneCountry,
            phoneLocal,
            domain,
            friendlyName,
            method,
            publicKey,
            secretKey,
            implStage,
        };
        localStorage.setItem(SESSION_KEY, JSON.stringify(session));
    }, [stage, onboardingId, onboardingToken, verifyToken, selectedPlatform, firstName, lastName, email, phone, phoneCountry, phoneLocal, domain, friendlyName, method, publicKey, secretKey, implStage]);

    // Restore session on mount
    useEffect(() => {
        if (!isInitialLoadDone || user) return;
        try {
            const raw = localStorage.getItem(SESSION_KEY);
            if (!raw) return;
            const s = JSON.parse(raw);
            if (!s?.expiresAt || Date.now() > s.expiresAt) {
                localStorage.removeItem(SESSION_KEY);
                return;
            }
            if (!s.stage || s.stage === "idle" || !STAGE_STEPS[s.stage]) return;

            if (s.firstName) setFirstName(s.firstName);
            if (s.lastName) setLastName(s.lastName);
            if (s.email) setEmail(s.email);
            if (s.phone) setPhone(s.phone);
            if (s.phoneCountry) setPhoneCountry(s.phoneCountry);
            if (s.phoneLocal) setPhoneLocal(s.phoneLocal);
            if (s.domain) setDomain(s.domain);
            if (s.friendlyName) setFriendlyName(s.friendlyName);
            if (s.method) setMethod(s.method);
            if (s.onboardingId) setOnboardingId(s.onboardingId);
            if (s.onboardingToken) setOnboardingToken(s.onboardingToken);
            if (s.verifyToken) setVerifyToken(s.verifyToken);
            if (s.selectedPlatform) setSelectedPlatform(s.selectedPlatform);
            if (s.publicKey) setPublicKey(s.publicKey);
            if (s.secretKey) setSecretKey(s.secretKey);
            if (s.implStage) setImplStage(s.implStage);

            setStage(s.stage);
            setMessages(
                STAGE_STEPS[s.stage].map((type) => ({
                    id: `restored-${type}`,
                    role: "assistant" as const,
                    type,
                }))
            );
        } catch {
            localStorage.removeItem(SESSION_KEY);
        }
    }, [isInitialLoadDone, user]);

    const applyState = (state?: AgentState) => {
        if (!state) return;
        if (state.verify_token) setVerifyToken(state.verify_token);
        if (state.public_key) setPublicKey(state.public_key);
        if (state.secret_key) setSecretKey(state.secret_key);
    };

    const pushMessage = (entry: ChatEntry) => {
        setMessages((prev) => [...prev, entry]);
    };

    const ensureStep = (type: ChatEntry["type"]) => {
        setMessages((prev) => {
            if (prev.some((m) => m.type === type)) return prev;
            return [...prev, { id: `${type}-${Date.now()}`, role: "assistant", type }];
        });
    };

    const startOnboarding = () => {
        if (stage !== "idle") return;
        setStage("contact");
        ensureStep("contact");
    };

    const resetOnboarding = () => {
        localStorage.removeItem(SESSION_KEY);
        setStage("idle");
        setVerifyToken(null);
        setOnboardingId(null);
        setOnboardingToken(null);
        setPublicKey(null);
        setSecretKey(null);
        setImplStage("keys");
        setShowWhatsNext(false);
        setSelectedPlatform(null);
        setContactError(null);
        setEmailError(null);
        setPhoneError(null);
        setStep1Error(null);
        setStep2Error(null);
        setStep3Error(null);
        setMessages((prev) => prev.filter((m) => m.type === "text"));
    };

    const getOnboardingContextText = () => {
        const defaultLang = uiLang === "he" ? "Hebrew" : "English";
        const langNote = `Respond in the same language the user writes in; default to ${defaultLang} if they haven't written yet.`;

        if (stage === "idle") {
            return [
                "=== ONBOARDING STATUS (authoritative — do NOT ask the user) ===",
                "page: onboarding-ai (public, no login required)",
                "registration_started: NO",
                "contact_details_submitted: NO",
                "email_verified: NO",
                "phone_verified: NO",
                "platform_selected: NO",
                "has_account: NO — this user has not created a Vuelve account yet",
                "next_step: user must click 'Start onboarding' or send a message to begin entering their contact details",
                "IMPORTANT: Do NOT mention account setup steps (phone number selection, pricing plan, WhatsApp/Meta connection). Those only happen after the app/plugin is installed and the user logs in to the dashboard.",
                "IMPORTANT: Never use the terms 'Phase 1' or 'Phase 2' with the user.",
                langNote,
            ].join("\n");
        }

        const completed: string[] = [];
        const next = (() => {
            if (stage === "contact") {
                return "entering contact details (first name, last name, email, phone, company, website)";
            }
            if (stage === "email") {
                completed.push("contact_details_submitted: YES");
                return "verifying email — code sent to their email address";
            }
            if (stage === "phone") {
                completed.push("contact_details_submitted: YES", "email_verified: YES");
                return "verifying phone number — code sent via SMS";
            }
            if (stage === "platform") {
                completed.push("contact_details_submitted: YES", "email_verified: YES", "phone_verified: YES");
                if (selectedPlatform) {
                    completed.push(`platform_selected: YES — detected as ${selectedPlatform}${websiteUrl ? ` (${websiteUrl})` : ""}`);
                    return `platform detected: ${selectedPlatform} — user has been shown install instructions and next steps`;
                }
                return "entering website URL — platform is being auto-detected from their domain";
            }
            completed.push("contact_details_submitted: YES", "email_verified: YES", "phone_verified: YES", "platform_selected: YES");
            if (stage === "step1") return "registration complete — user should log in to the Vuelve dashboard at app.vuelve.co to continue account setup (phone number, pricing plan, WhatsApp connection)";
            if (stage === "step2") return "verifying domain ownership (meta tag / DNS / HTML file)";
            return "registration complete — user should log in to the Vuelve dashboard at app.vuelve.co to continue account setup";
        })();

        const completedLines = completed.length ? completed.join("\n") : "none yet";
        return [
            "=== ONBOARDING STATUS (authoritative — do NOT ask the user) ===",
            "page: onboarding-ai (public registration, no login required)",
            "has_account: NO — user is mid registration, no login yet",
            completedLines,
            `current_step: ${next}`,
            "IMPORTANT: Do NOT mention account setup steps (phone number selection, pricing plan, WhatsApp/Meta connection) unless the platform is already detected and the app/plugin install instructions have been shown.",
            "IMPORTANT: Never use the terms 'Phase 1' or 'Phase 2' with the user.",
            langNote,
        ].join("\n");
    };

    const handleSend = async () => {
        if (isThinking) return;
        if (!inputText.trim()) return;
        const text = inputText.trim();
        setInputText("");
        pushMessage({ id: `u-${Date.now()}`, role: "user", type: "text", text });

        if (/start over|restart onboarding|reset onboarding|start again/i.test(text)) {
            resetOnboarding();
            startOnboarding();
            return;
        }

        setIsThinking(true);
        const isNewSession = !conversationId;
        let turnstileToken: string | undefined;
        const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
        if (isNewSession && !isLocalhost) {
            turnstileToken = await getTurnstileToken() || undefined;
            console.log("[turnstile] token for request:", turnstileToken?.slice(0, 20));
        }
        const res = await postAgentChat(apiDomain, {
            conversation_id: conversationId,
            message: text,
            turnstile_token: isNewSession ? turnstileToken : undefined,
            input: {
                onboarding_context: getOnboardingContextText(),
            },
        });
        setIsThinking(false);
        if (!res.ok) {
            message.error(res.error || "Agent failed");
            return;
        }
        setConversationId(res.data!.conversation_id);
        if (res.data!.start_onboarding) {
            startOnboarding();
            return;
        }
        const rtl =
            Boolean(res.data!.is_rtl) ||
            /[\u0590-\u05FF\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/.test(res.data!.reply || "");
        pushMessage({
            id: `a-${Date.now()}`,
            role: "assistant",
            type: "text",
            text: res.data!.reply || "",
            action: res.data!.action || "none",
            actionLabel: res.data!.action_label || undefined,
            language: res.data!.language || undefined,
            isRtl: rtl,
            pricingPlans: res.data!.pricing_plans,
        });
    };

    const contactLocked = stage !== "contact";
    const emailLocked = stage !== "email";
    const phoneLocked = stage !== "phone";
    const platformLocked = stage !== "platform";
    const step1Locked = stage !== "step1";
    const step2Locked = stage !== "step2";
    const step3Locked = stage !== "step3";

    const contactForm = (
        <div className={`v2oa-step ${contactLocked ? "v2oa-locked" : ""}`}>
            <div className="v2oa-step-title"><span>1</span>{tr.contact_title}</div>
            <div className="v2oa-field-step-1">
                <div className="v2oa-field-main">
                    <Text className="v2oa-label">{tr.first_name}</Text>
                    <Input
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        placeholder={tr.first_name_ph}
                        disabled={contactLocked}
                    />
                </div>
            </div>
            <div className="v2oa-field-step-1">
                <div className="v2oa-field-main">
                    <Text className="v2oa-label">{tr.last_name}</Text>
                    <Input
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        placeholder={tr.last_name_ph}
                        disabled={contactLocked}
                    />
                </div>
            </div>
            <div className="v2oa-field-step-1">
                <div className="v2oa-field-main">
                    <Text className="v2oa-label">{tr.email}</Text>
                    <Input
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@company.com"
                        disabled={contactLocked}
                    />
                </div>
            </div>
            <div className="v2oa-field-step-1">
                <div className="v2oa-field-main">
                    <Text className="v2oa-label">{tr.phone}</Text>
                    <div className="custom-number-container-inner">
                        <CountriesSelector
                            isDisabled={contactLocked}
                            selectedCountry={phoneCountry}
                            setSelectedCountry={setPhoneCountry}
                            valueProp="country"
                            hideCountryName={true}
                        />
                        <span className="phone-separator">-</span>
                        <Input
                            disabled={contactLocked}
                            placeholder={tr.phone_ph}
                            maxLength={14}
                            value={phoneLocal}
                            onChange={(e) => setPhoneLocal(cleanPhoneNumberOnlyDigits(e.target.value))}
                        />
                    </div>
                </div>
            </div>
            {contactError ? <div className="v2oa-error"><span>{contactError}</span></div> : null}
            <Button
                type="primary"
                loading={loading}
                size="large"
                onClick={async () => {
                    setContactError(null);
                    if (!firstName.trim()) return setContactError(tr.err_first_name);
                    if (!lastName.trim()) return setContactError(tr.err_last_name);
                    if (!email.trim()) return setContactError(tr.err_email);
                    if (!phoneLocal.trim()) return setContactError(tr.err_phone);
                    setLoading(true);
                    try {
                        const phoneE164 = buildE164(phoneCountry, phoneLocal);
                        if (!phoneE164) {
                            setContactError(tr.err_phone_invalid);
                            return;
                        }
                        const res = await StartV2OnboardingContact(apiDomain, {
                            first_name: firstName.trim(),
                            last_name: lastName.trim(),
                            email: email.trim(),
                            phone: phoneE164,
                        });
                        if (!res.ok) {
                            setContactError(res.error || tr.err_contact_save);
                            return;
                        }
                        setOnboardingId(res.data?.onboarding_id || null);
                        setOnboardingToken(res.data?.onboarding_token || null);
                        setPhone(phoneE164);
                        const sendRes = await SendV2OnboardingEmailCode(apiDomain, res.data?.onboarding_token || "", email.trim());
                        if (!sendRes.ok) {
                            setContactError(sendRes.error || tr.err_email_send);
                            return;
                        }
                        setStage("email");
                        ensureStep("email");
                    } finally {
                        setLoading(false);
                    }
                }}
                disabled={contactLocked}
            >
                {tr.btn_continue_email}
            </Button>
        </div>
    );

    const emailForm = (
        <div className={`v2oa-step ${emailLocked ? "v2oa-locked" : ""}`}>
            <div className="v2oa-step-title"><span>2</span>{tr.email_title}</div>
            <div className="v2oa-field-step-1">
                <div className="v2oa-field-main">
                    <Text className="v2oa-label">{tr.email}</Text>
                    <Input
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@company.com"
                        disabled={emailLocked}
                    />
                </div>
            </div>
            <div className="v2oa-field-step-1">
                <div className="v2oa-field-main">
                    <Text className="v2oa-label">{tr.verification_code}</Text>
                    <Input
                        value={emailCode}
                        onChange={(e) => setEmailCode(e.target.value)}
                        placeholder="123456"
                        disabled={emailLocked}
                    />
                </div>
            </div>
            {emailError ? <div className="v2oa-error"><span>{emailError}</span></div> : null}
            <div className="v2oa-actions">
                <Button
                    type="default"
                    loading={loading}
                    className="resend-btn"
                    onClick={async () => {
                        setEmailError(null);
                        if (!onboardingToken) return setEmailError(tr.err_missing_token);
                        setLoading(true);
                        try {
                            const res = await SendV2OnboardingEmailCode(apiDomain, onboardingToken, email.trim());
                            if (!res.ok) {
                                setEmailError(res.error || tr.err_email_send);
                                return;
                            }
                        } finally {
                            setLoading(false);
                        }
                    }}
                    disabled={emailLocked}
                >
                    {tr.btn_resend}
                </Button>
                <Button
                    type="primary"
                    loading={loading}
                    onClick={async () => {
                        setEmailError(null);
                        if (!onboardingToken) return setEmailError(tr.err_missing_token);
                        if (!emailCode.trim()) return setEmailError(tr.err_enter_code);
                        setLoading(true);
                        try {
                            const res = await VerifyV2OnboardingEmailCode(apiDomain, onboardingToken, emailCode.trim());
                            if (!res.ok || !res.data?.verified) {
                                setEmailError(res.error || tr.err_invalid_code);
                                return;
                            }
                            const phoneE164 = buildE164(phoneCountry, phoneLocal) || phone.trim();
                            if (phoneE164) {
                                await SendV2OnboardingPhoneCode(apiDomain, onboardingToken, phoneE164);
                                setPhone(phoneE164);
                            }
                            setStage("phone");
                            ensureStep("phone");
                        } finally {
                            setLoading(false);
                        }
                    }}
                    disabled={emailLocked}
                >
                    {tr.btn_verify_email}
                </Button>
            </div>
        </div>
    );

    const phoneForm = (
        <div className={`v2oa-step ${phoneLocked ? "v2oa-locked" : ""}`}>
            <div className="v2oa-step-title"><span>3</span>{tr.phone_title}</div>
            <div className="v2oa-field-step-1">
                <div className="v2oa-field-main">
                    <Text className="v2oa-label">{tr.phone}</Text>
                    <div className="custom-number-container-inner">
                        <CountriesSelector
                            isDisabled={phoneLocked}
                            selectedCountry={phoneCountry}
                            setSelectedCountry={setPhoneCountry}
                            valueProp="country"
                            hideCountryName={true}
                        />
                        <span className="phone-separator">-</span>
                        <Input
                            disabled={phoneLocked}
                            placeholder="Phone Number"
                            maxLength={14}
                            value={phoneLocal}
                            onChange={(e) => setPhoneLocal(cleanPhoneNumberOnlyDigits(e.target.value))}
                        />
                    </div>
                </div>
            </div>
            <div className="v2oa-field-step-1">
                <div className="v2oa-field-main">
                    <Text className="v2oa-label">{tr.verification_code}</Text>
                    <Input
                        value={phoneCode}
                        onChange={(e) => setPhoneCode(e.target.value)}
                        placeholder="123456"
                        disabled={phoneLocked}
                    />
                </div>
            </div>
            {phoneError ? <div className="v2oa-error"><span>{phoneError}</span></div> : null}
            <div className="v2oa-actions">
                <Button
                    type="default"
                    loading={loading}
                    className="resend-btn"
                    onClick={async () => {
                        setPhoneError(null);
                        if (!onboardingToken) return setPhoneError(tr.err_missing_token);
                        setLoading(true);
                        try {
                            const phoneE164 = buildE164(phoneCountry, phoneLocal);
                            if (!phoneE164) {
                                setPhoneError(tr.err_phone_invalid);
                                return;
                            }
                            const res = await SendV2OnboardingPhoneCode(apiDomain, onboardingToken, phoneE164);
                            if (!res.ok) {
                                setPhoneError(res.error || tr.err_phone_send);
                                return;
                            }
                            setPhone(phoneE164);
                        } finally {
                            setLoading(false);
                        }
                    }}
                    disabled={phoneLocked}
                >
                    {tr.btn_resend}
                </Button>
                <Button
                    type="primary"
                    loading={loading}
                    onClick={async () => {
                        setPhoneError(null);
                        if (!onboardingToken) return setPhoneError(tr.err_missing_token);
                        if (!phoneCode.trim()) return setPhoneError(tr.err_enter_code);
                        setLoading(true);
                    try {
                        const res = await VerifyV2OnboardingPhoneCode(apiDomain, onboardingToken, phoneCode.trim());
                            if (!res.ok || !res.data?.verified) {
                                setPhoneError(res.error || tr.err_invalid_code);
                                return;
                            }
                            setStage("platform");
                            ensureStep("platform");
                        } finally {
                            setLoading(false);
                        }
                    }}
                    disabled={phoneLocked}
                >
                    {tr.btn_verify_phone}
                </Button>
            </div>
        </div>
    );

    const platformForm = (
        <div className={`v2oa-step ${platformLocked ? "v2oa-locked" : ""}`}>
            <div className="v2oa-step-title"><span>4</span>{tr.platform_title}</div>
            {!selectedPlatform ? (
                <>
                    <div className="v2oa-field-step-1">
                        <div className="v2oa-field-main">
                            <Text className="v2oa-label">{tr.platform_url_label}</Text>
                            <Input
                                value={websiteUrl}
                                onChange={(e) => setWebsiteUrl(e.target.value)}
                                placeholder={tr.platform_url_ph}
                                disabled={platformLocked || isDetecting}
                                onPressEnter={async () => {
                                    if (!websiteUrl.trim()) return setPlatformError(tr.platform_err_missing);
                                    setPlatformError(null);
                                    setIsDetecting(true);
                                    const res = await DetectV2OnboardingPlatform(apiDomain, onboardingToken || "", websiteUrl.trim());
                                    setIsDetecting(false);
                                    if (!res.ok || !res.data?.platform) return setPlatformError(tr.platform_err_failed);
                                    setSelectedPlatform(res.data.platform);
                                }}
                            />
                        </div>
                        <div className="v2oa-help">{tr.platform_url_help}</div>
                    </div>
                    {platformError ? <div className="v2oa-error"><span>{platformError}</span></div> : null}
                    <Button
                        type="primary"
                        loading={isDetecting}
                        size="large"
                        disabled={platformLocked}
                        onClick={async () => {
                            if (!websiteUrl.trim()) return setPlatformError(tr.platform_err_missing);
                            setPlatformError(null);
                            setIsDetecting(true);
                            const res = await DetectV2OnboardingPlatform(apiDomain, onboardingToken || "", websiteUrl.trim());
                            setIsDetecting(false);
                            if (!res.ok || !res.data?.platform) return setPlatformError(tr.platform_err_failed);
                            setSelectedPlatform(res.data.platform);
                        }}
                    >
                        {isDetecting ? tr.platform_detecting : tr.platform_btn}
                    </Button>
                </>
            ) : null}

            {selectedPlatform === "shopify" ? (
                <div className="v2oa-platform-result v2oa-platform-result-shopify">
                    <div className="v2oa-platform-result-icon">
                        <img src={ShopifyIconPng} alt="Shopify" className="v2oa-platform-logo" />
                    </div>
                    <div className="v2oa-platform-result-text">
                        {tr.shopify_result}
                    </div>
                    <a href="https://apps.shopify.com/vuelve" target="_blank" rel="noreferrer">
                        <button className="v2oa-platform-cta v2oa-platform-cta-shopify">
                            {tr.shopify_cta}
                            <svg className="v2oa-cta-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
                        </button>
                    </a>
                    <div className="v2oa-platform-next-steps">
                        <p>{tr.shopify_next}</p>
                    </div>
                </div>
            ) : null}

            {selectedPlatform === "woocommerce" ? (
                <div className="v2oa-platform-result v2oa-platform-result-woo">
                    <div className="v2oa-platform-result-icon">
                        <img src={WooIcon} alt="WooCommerce" className="v2oa-platform-logo" />
                    </div>
                    <div className="v2oa-platform-result-text">
                        {tr.woo_result}
                    </div>
                    <a href="https://wordpress.org/plugins/vuelve" target="_blank" rel="noreferrer">
                        <button className="v2oa-platform-cta v2oa-platform-cta-woo">
                            {tr.woo_cta}
                            <svg className="v2oa-cta-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
                        </button>
                    </a>
                    <div className="v2oa-platform-next-steps">
                        <p>{tr.woo_next_1}</p>
                        <p>{tr.woo_next_2}</p>
                    </div>
                </div>
            ) : null}

            {selectedPlatform === "standalone" ? (
                <div className="v2oa-platform-result v2oa-platform-result-standalone">
                    <div className="v2oa-platform-result-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <circle cx="12" cy="12" r="10" />
                            <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                        </svg>
                    </div>
                    <div className="v2oa-platform-result-text">
                        {tr.standalone_result}
                    </div>
                </div>
            ) : null}
        </div>
    );

    const step1Form = (
        <div className={`v2oa-step ${step1Locked ? "v2oa-locked" : ""}`}>
            <div className="v2oa-step-title"><span>5</span>Register your website</div>
            <div className="v2oa-field">
                <div className="v2oa-field-main">
                    <Text className="v2oa-label">Domain</Text>
                    <Input
                        value={domain}
                        onChange={(e) => setDomain(e.target.value)}
                        placeholder="example.com"
                        disabled={step1Locked}
                    />
                </div>
                <div className="v2oa-help">
                    Your public website domain (used to verify ownership and generate your tracking keys).
                </div>
            </div>
            <div className="v2oa-field">
                <div className="v2oa-field-main">
                    <Text className="v2oa-label">Shop name</Text>
                    <Input
                        value={friendlyName}
                        onChange={(e) => setFriendlyName(e.target.value)}
                        placeholder="Company name"
                        disabled={step1Locked}
                    />
                </div>
                <div className="v2oa-help">
                    The display name for your business inside Vuelve (used in dashboard and messages).
                </div>
            </div>
            <div className="v2oa-field">
                <div className="v2oa-field-main">
                    <Text className="v2oa-label">Verification method</Text>
                    <Select
                        value={method}
                        className="v2oa-select"
                        onChange={(v) => setMethod(v)}
                        disabled={step1Locked}
                        options={[
                            { value: "meta", label: "Meta tag (recommended)" },
                            { value: "dns", label: "DNS TXT" },
                            { value: "html", label: "HTML file" },
                        ]}
                    />
                </div>
                <div className="v2oa-help">
                    How we confirm you control the domain.
                    Meta tag adds a verification tag to your homepage.
                    DNS TXT adds a verification record in your DNS.
                    HTML file uploads a verification file to `/.well-known/`.
                </div>
            </div>
            {step1Error ? <div className="v2oa-error">{step1Error}</div> : null}
            <Button
                type="primary"
                loading={loading}
                size="large"
                onClick={async () => {
                    setStep1Error(null);
                    if (!domain.trim()) return setStep1Error("Domain is required");
                    if (!onboardingToken) return setStep1Error("Complete contact verification first");
                    setLoading(true);
                    try {
                        const res = await RegisterV2Onboarding(
                            domain.trim(),
                            friendlyName.trim(),
                            method,
                            2,
                            onboardingToken
                        );
                        if (!res.ok) {
                            setStep1Error(res.error || "Failed to register");
                            return;
                        }
                        setVerifyToken(res.data?.verify_token || null);
                        setOnboardingId(res.data?.onboarding_id || null);
                        setOnboardingToken(res.data?.onboarding_token || null);
                        setStage("step2");
                        ensureStep("step2");
                    } finally {
                        setLoading(false);
                    }
                }}
                disabled={step1Locked}
            >
                Create verification
            </Button>
        </div>
    );

    const step2Form = (
        <div className={`v2oa-step ${step2Locked ? "v2oa-locked" : ""}`}>
            <div className="v2oa-step-title"><span>6</span>Verify domain</div>
            {(() => {
                const guide = method === "meta"
                    ? "Add this meta tag inside the <head> of your homepage. It proves you control the domain. When you're done - click the Check Verification button below."
                    : method === "dns"
                        ? "Add a DNS TXT record on your domain with the value below. It proves you own the domain. When you're done - click the Check Verification button below."
                        : "Create a file at /.well-known/vuelve-verify.txt with the exact token below. It proves you control the site. When you're done - click the Check Verification button below.";
                return <div className="v2oa-help s2-help">{guide}</div>;
            })()}
            {(() => {
                const code = method === "meta"
                    ? `<meta name="vuelve-verify" content="${verifyToken || "TOKEN"}" />`
                    : method === "dns"
                        ? `TXT record: vuelve-verify=${verifyToken || "TOKEN"}`
                        : `/.well-known/vuelve-verify.txt\n${verifyToken || "TOKEN"}`;
                return (
                    <div className="v2oa-code-block">
                        <CopyToClipboardCode content={code} />
                    </div>
                );
            })()}
            {step2Error ? <div className="v2oa-error"><span>{step2Error}</span></div> : null}
            <Button
                type="primary"
                loading={loading}
                onClick={async () => {
                    setStep2Error(null);
                    if (!onboardingId || !onboardingToken) return setStep2Error("Register first");
                    setLoading(true);
                    try {
                        const res = await VerifyV2Onboarding(apiDomain, onboardingId, onboardingToken);
                        if (!res.ok) {
                            setStep2Error(res.error || "Verify failed");
                            return;
                        }
                        if (!res.data?.verified) {
                            setStep2Error("I couldn’t verify it yet. Please double-check and try again.");
                            return;
                        }
                        if (res.data?.user?.token) {
                            localStorage.setItem("token", res.data.user.token);
                            localStorage.removeItem(SESSION_KEY);
                            window.location.href = "/dashboard";
                            return;
                        }
                        setStep2Error("Verified, but login failed. Please try logging in.");
                    } finally {
                        setLoading(false);
                    }
                }}
                disabled={step2Locked}
            >
                Check verification
            </Button>
        </div>
    );


    const keyImplementationBlock = (
        <div className="v2oa-step">
            <div className="v2oa-step-title"><span>7</span>Keys</div>
            <div className="v2oa-guide">Your public key is used in the browser SDK, while the secret key is used only on your server for Identify calls. Keep the secret key private and store it securely.</div>
            <div className="v2oa-step-3-row">
                <div className="v2oa-ki-row">
                    <div className="v2oa-ki-col">
                        {publicKey ? (
                            <div className="v2oa-code-block">
                                <label>Public key (browser SDK)</label>
                                <CopyToClipboardCode content={publicKey} />
                                <div className="v2oa-help">Public key is used in the browser SDK</div>
                            </div>
                        ) : null}
                    </div>
                    <div className="v2oa-ki-col">
                        {secretKey ? (
                            <div className="v2oa-code-block">
                                <label>Secret key (server identify)</label>
                                <CopyToClipboardCode content={secretKey} />
                                <div className="v2oa-help">Secret key is used only on your server to identify users.</div>
                                <div className="v2oa-help">Store the secret key safely. You won’t see it again.</div>
                            </div>
                        ) : null}
                        <Text type="secondary"></Text>
                    </div>
                </div>
            </div>
            {implStage === "keys" ? (
                <Button type="primary" onClick={() => setImplStage("server")}>
                    Continue to server-side setup
                </Button>
            ) : null}
            {implStage === "server" || implStage === "client" ? (
                <div id="snippets" className="v2oa-ki-row">
                    <div className="v2oa-ki-col">
                        <div className="subtitle">Server-side implementation</div>
                        <div className="v2oa-guide">Server‑side identification is required so Vuelve can reliably connect a user to their real email/phone and consent status. Call this from your backend whenever a user signs up, logs in, or updates their
                            profile/consents. It keeps your WhatsApp/SMS/email permissions and contact details accurate.
                        </div>
                        <div className="v2oa-step-3-row">
                            <label>Identify users from your server (connect user to email/phone)</label>
                            <CopyToClipboardCode maxLength={100000} content={`POST /v2/identify (Bearer ${secretKey || "vlv_sec_..."})\n{\n  "client_id": "user_123",\n  "email": "a@b.com",\n  "phone": "+14155551234",\n  "email_consent": true,\n  "sms_consent": false,\n  "first_name": "Jane",\n  "last_name": "Doe",\n  "full_name": "Jane Doe",\n  "dob": "1990-01-31"\n}`} />
                            <label>Track backend events from your server (trusted events)</label>
                            <CopyToClipboardCode maxLength={100000} content={`POST /v2/track (Bearer ${secretKey || "vlv_sec_..."})\n{\n  "client_id": "user_123",\n  "event_name": "order.created",\n  "entity_type": "order",\n  "entity_id": "98765",\n  "meta": {\n    "context": {\n      "order_id": "98765",\n      "order_total": 149.9,\n      "currency": "USD"\n    }\n  }\n}`} />
                            <Tabs
                                size="small"
                                items={[
                                    {
                                        key: "node",
                                        label: "Node.js",
                                        children: (
                                            <CopyToClipboardCode maxLength={100000} content={`const axios = require("axios");\n\nawait axios.post("https://YOUR_SERVER/v2/identify", {\n  client_id: "user_123",\n  email: "a@b.com",\n  phone: "+14155551234",\n  email_consent: true,\n  sms_consent: false,\n  first_name: "Jane",\n  last_name: "Doe",\n  full_name: "Jane Doe",\n  dob: "1990-01-31"\n}, {\n  headers: { Authorization: "Bearer ${secretKey || "vlv_sec_..."}" }\n});`} />
                                        ),
                                    },
                                    {
                                        key: "python",
                                        label: "Python",
                                        children: (
                                            <CopyToClipboardCode maxLength={100000} content={`import requests\n\nrequests.post(\n  "https://YOUR_SERVER/v2/identify",\n  json={\n    "client_id": "user_123",\n    "email": "a@b.com",\n    "phone": "+14155551234",\n    "email_consent": true,\n    "sms_consent": false,\n    "first_name": "Jane",\n    "last_name": "Doe",\n    "full_name": "Jane Doe",\n    "dob": "1990-01-31"\n  },\n  headers={"Authorization": "Bearer ${secretKey || "vlv_sec_..."}"}\n)`} />
                                        ),
                                    },
                                    {
                                        key: "java",
                                        label: "Java",
                                        children: (
                                            <CopyToClipboardCode maxLength={100000} content={`HttpRequest request = HttpRequest.newBuilder()\n  .uri(URI.create("https://YOUR_SERVER/v2/identify"))\n  .header("Authorization", "Bearer ${secretKey || "vlv_sec_..."}")\n  .header("Content-Type", "application/json")\n  .POST(HttpRequest.BodyPublishers.ofString(\n    "{\\"client_id\\":\\"user_123\\",\\"email\\":\\"a@b.com\\",\\"phone\\":\\"+14155551234\\",\\"email_consent\\":true,\\"sms_consent\\":false,\\"first_name\\":\\"Jane\\",\\"last_name\\":\\"Doe\\",\\"full_name\\":\\"Jane Doe\\",\\"dob\\":\\"1990-01-31\\"}"\n  ))\n  .build();\n\nHttpClient.newHttpClient().send(request, HttpResponse.BodyHandlers.ofString());`} />
                                        ),
                                    },
                                    {
                                        key: "php",
                                        label: "PHP",
                                        children: (
                                            <CopyToClipboardCode maxLength={100000} content={`$ch = curl_init("https://YOUR_SERVER/v2/identify");\ncurl_setopt($ch, CURLOPT_HTTPHEADER, [\n  "Authorization: Bearer ${secretKey || "vlv_sec_..."}",\n  "Content-Type: application/json"\n]);\ncurl_setopt($ch, CURLOPT_POST, true);\ncurl_setopt($ch, CURLOPT_POSTFIELDS, json_encode([\n  "client_id" => "user_123",\n  "email" => "a@b.com",\n  "phone" => "+14155551234",\n  "email_consent" => true,\n  "sms_consent" => false,\n  "first_name" => "Jane",\n  "last_name" => "Doe",\n  "full_name" => "Jane Doe",\n  "dob" => "1990-01-31"\n]));\ncurl_setopt($ch, CURLOPT_RETURNTRANSFER, true);\n$response = curl_exec($ch);\ncurl_close($ch);`} />
                                        ),
                                    },
                                ]}
                            />
                        </div>
                    </div>
                </div>
            ) : null}
            {implStage === "server" ? (
                <Button type="primary" onClick={() => setImplStage("client")}>
                    Continue to client-side setup
                </Button>
            ) : null}
            {implStage === "client" ? (
                <div id="client-snippets" className="v2oa-ki-row">
                    <div className="v2oa-ki-col">
                        <div className="subtitle">Client-side implementation</div>
                        <div className="v2oa-guide">
                            Client‑side tracking powers your automations by sending user actions (views, clicks, purchases) from the browser to Vuelve. Add the SDK once site‑wide, then call track on key events.
                        </div>
                        <div className="v2oa-step-3-row">
                            <Tabs
                                size="small"
                                items={[
                                    {
                                        key: "npm",
                                        label: "NPM (recommended)",
                                        children: (
                                            <div>
                                                <label>Install</label>
                                                <CopyToClipboardCode content={`npm i @vuelve/sdk`} />
                                                <label>Initialize</label>
                                                <CopyToClipboardCode content={`import { loadVuelve, identify, track } from "@vuelve/sdk";\n\nloadVuelve({\n  publicKey: "${publicKey || "vlv_pub_..."}",\n  baseUrl: "http://vuelve-server.fly.dev"\n});`} />
                                                <label>Use</label>
                                                <CopyToClipboardCode content={`identify({ client_id: "user_123" });`} />
                                                <CopyToClipboardCode content={`track("room.view", { context: { room_name: "Suite" } });`} />
                                            </div>
                                        ),
                                    },
                                    {
                                        key: "script",
                                        label: "Script tag",
                                        children: (
                                            <div>
                                                <label>Install</label>
                                                <CopyToClipboardCode content={`<script src="https://cdn.your-domain.com/vuelve/sdk.js?key=${publicKey || "vlv_pub_..."}&baseUrl=http://vuelve-server.fly.dev" async></script>`} />
                                                <label>TypeScript types (optional)</label>
                                                <CopyToClipboardCode maxLength={1000} content={`// src/types/vuelve.d.ts\nexport {};\n\ndeclare global {\n  interface Window {\n    vuelve?: {\n      identify: (payload: {\n        client_id?: string;\n        email?: string;\n        phone?: string;\n        first_name?: string;\n        last_name?: string;\n        full_name?: string;\n      }) => void;\n      track: (event_name: string, meta?: any) => void;\n    };\n  }\n}\n`} />
                                                <label>Use</label>
                                                <CopyToClipboardCode content={`window.vuelve?.identify({ client_id: "user_123" });`} />
                                                <CopyToClipboardCode content={`window.vuelve?.track("room.view", { context: { room_name: "Suite" } });`} />
                                            </div>
                                        ),
                                    },
                                ]}
                            />
                        </div>
                    </div>
                </div>
            ) : null}
            {implStage === "client" ? (
                <Button
                    type="primary"
                    onClick={() => {
                        window.open("/login", "_blank");
                    }}
                    icon={<LoginIcon />}
                >
                    Login to your new account
                </Button>
            ) : null}
        </div>
    );

    const renderMessage = (m: ChatEntry) => {
        if (m.type === "contact") return contactForm;
        if (m.type === "email") return emailForm;
        if (m.type === "phone") return phoneForm;
        if (m.type === "platform") return platformForm;
        if (m.type === "step1") return step1Form;
        if (m.type === "step2") return step2Form;
        if (m.type === "keys") return keyImplementationBlock;
        if (m.action === "show_pricing" && m.pricingPlans?.length) {
            return (
                <div className={`v2oa-text ${isRtlUI ? "v2oa-rtl" : ""}`} dir={isRtlUI ? "rtl" : "ltr"}>
                    <div className="v2oa-pricing-title">{tr.pricing_title}</div>
                    <div className="v2oa-pricing-plans">
                        {m.pricingPlans.map((plan) => (
                            <div key={plan.id} className="v2oa-pricing-card">
                                {plan.badge ? <div className="v2oa-pricing-badge">{plan.badge}</div> : null}
                                <div className="v2oa-pricing-plan-name">{plan.title}</div>
                                <div className="v2oa-pricing-fee">
                                    <span className="v2oa-pricing-amount">${plan.fee}</span>
                                    <span className="v2oa-pricing-period">{tr.pricing_per_month}</span>
                                </div>
                                {plan.freeCreditPerCycle ? <div className="v2oa-pricing-cap">{plan.freeCreditPerCycle.toLocaleString()} {tr.pricing_marketing}</div> : null}
                                {plan.freeConversationMessagesPerCycle ? <div className="v2oa-pricing-cap">{plan.freeConversationMessagesPerCycle.toLocaleString()} {tr.pricing_conversation}</div> : null}
                                <div className="v2oa-pricing-phone">✓ {tr.pricing_phone_included}</div>
                                <Button className="v2oa-pricing-cta" type="primary" onClick={startOnboarding}>
                                    {tr.start_onboarding}
                                </Button>
                            </div>
                        ))}
                    </div>
                    <div className="v2oa-pricing-note">{tr.pricing_non_us_note}</div>
                </div>
            );
        }

        return (
            <div className={`v2oa-text ${m.isRtl ? "v2oa-rtl" : ""}`} dir={m.isRtl ? "rtl" : "ltr"}>
                {m.text}
                {m.action && m.action !== "none" && m.action !== "show_pricing" ? (
                    <div className="v2oa-cta">
                        {m.action === "start_onboarding" ? (
                            <Button type="primary" onClick={startOnboarding} disabled={stage !== "idle"}>
                                {m.actionLabel || "Start onboarding"}
                            </Button>
                        ) : (
                            <Button type="primary" onClick={() => { window.location.href = "/login"; }}>
                                {m.actionLabel || "Login"}
                            </Button>
                        )}
                    </div>
                ) : null}
            </div>
        );
    };

    if(user) {
        return <Navigate to="/" replace />
    }

    return (
        <div className="v2oa-page" dir={isRtlUI ? "rtl" : "ltr"}>
            <div ref={turnstileRef} style={{ display: "none" }} />
            <div className="bg" />
            <div className={`v2oa-body ${hasMessages ? "v2oa-body-active" : "v2oa-body-idle"}`}>
                {hasMessages ? (
                    <div className="v2oa-logo-corner">
                        <img src={VuelveLogo} width={120} />
                    </div>
                ) : null}
                {true ? (
                    <div className="v2oa-lang-corner">
                        <details className="v2oa-lang-picker">
                            <summary className="v2oa-lang-trigger">
                                <img className="v2oa-lang-flag" src={currentLangOption.flagSrc} alt="" aria-hidden="true" />
                                <span className="v2oa-lang-label">{currentLangOption.label}</span>
                            </summary>
                            <div className="v2oa-lang-menu" role="listbox" aria-label="Select language">
                                {LANG_OPTIONS.map((opt) => (
                                    <button
                                        key={opt.code}
                                        className={`v2oa-lang-option${uiLang === opt.code ? " v2oa-lang-option-active" : ""}`}
                                        onClick={() => {
                                            setUiLang(opt.code);
                                            localStorage.setItem(LANG_KEY, opt.code);
                                            document.querySelectorAll(".v2oa-lang-picker").forEach((el) => (el as HTMLDetailsElement).removeAttribute("open"));
                                        }}
                                        aria-current={uiLang === opt.code ? "true" : undefined}
                                    >
                                        <img className="v2oa-lang-flag" src={opt.flagSrc} alt="" aria-hidden="true" />
                                        <span className="v2oa-lang-label">{opt.label}</span>
                                    </button>
                                ))}
                            </div>
                        </details>
                        {hasMessages ? (
                            <Tooltip title={tr.new_onboarding_tooltip} placement="left" overlayClassName={isRtlUI ? "v2oa-tooltip-he" : ""}>
                                <button className="v2oa-restart-btn" onClick={() => { resetOnboarding(); }}>
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M12 20h9" />
                                        <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                                    </svg>
                                </button>
                            </Tooltip>
                        ) : null}
                    </div>
                ) : null}
                <div className="v2oa-guidance">
                    <img src={VuelveLogo} width={240} />
                    <div className="v2oa-title">{tr.idle_title}</div>
                    <div className="v2oa-guidance-links">
                        <Button type="link" className="starter-button" onClick={() => { window.location.href = "/login"; }}>
                            {tr.login}
                        </Button>
                        <Button type="link" className="starter-button" onClick={() => { window.open("https://vuelve.co") }}>
                            {tr.back_to_website}
                        </Button>
                        <Button type="link" className="starter-button" onClick={startOnboarding}>
                            {tr.start_onboarding}
                        </Button>
                    </div>
                </div>

                <div
                    className="v2oa-messages"
                    ref={messagesRef}
                    onScroll={(e) => {
                        const el = e.currentTarget;
                        const threshold = 24;
                        autoScrollRef.current = el.scrollHeight - el.scrollTop - el.clientHeight <= threshold;
                    }}
                >
                    {messages.map((m, idx) => {
                        const content = renderMessage(m);
                        if (!content) return null;
                        const isLastAssistant = m.role === "assistant" && idx === messages.length - 1 && m.type === "text";
                        return (
                            <div key={m.id} className={`v2oa-msg v2oa-${m.role}${m.isRtl ? " v2oa-msg-rtl" : ""}`}>
                                <div className="v2oa-bubble">
                                    {content}
                                    {isLastAssistant && stage === "idle" ? (
                                        <div className="v2oa-inline-actions">
                                            <Button type="primary" onClick={startOnboarding} disabled={stage !== "idle"}>
                                                {tr.start_onboarding}
                                            </Button>
                                            <Button type="default" onClick={() => { window.location.href = "/login"; }}>
                                                {tr.login}
                                            </Button>
                                        </div>
                                    ) : null}
                                    {isLastAssistant && stage !== "idle" ? (
                                        <div className="v2oa-inline-actions">
                                            <Button type="default" onClick={scrollToLastOnboarding}>
                                                {tr.back_to_onboarding}
                                            </Button>
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                        );
                    })}
                    {isThinking ? (
                        <div className="v2oa-msg v2oa-assistant">
                            <div className="v2oa-bubble">
                                <div className="v2oa-loader">
                                    <span />
                                    <span />
                                    <span />
                                </div>
                            </div>
                        </div>
                    ) : null}
                    <div id="messages-spacer" className="v2oa-scroll-spacer" />
                </div>

                <div className={`v2oa-input-stack ${hasMessages ? "v2oa-input-stack-bottom" : ""}`}>
                    <div className={`v2oa-input ${hasMessages ? "v2oa-input-bottom" : "v2oa-input-centered"}`}>
                        <Input.TextArea
                            rows={1}
                            autoSize={{ minRows: 1, maxRows: 6 }}
                            value={inputText}
                            onChange={(e) => setInputText(e.target.value)}
                            placeholder={tr.placeholder}
                            disabled={inputDisabled}
                            onPressEnter={(e) => {
                                if (e.shiftKey) return;
                                e.preventDefault();
                                handleSend();
                            }}
                        />
                        <Button
                            type="primary"
                            shape="circle"
                            icon={<SendFilledIcon />}
                            onClick={handleSend}
                            disabled={inputDisabled}
                            aria-label="Send"
                            className="v2oa-send-button"
                        />
                    </div>
                    {hasMessages && stage === "idle" ? (
                        <div className="v2oa-starter-bottom">
                            <Button type="link" className="starter-button" onClick={() => { window.location.href = "/login"; }}>
                                {tr.login}
                            </Button>
                            <Button type="link" className="starter-button" onClick={() => { window.open("https://vuelve.co") }}>
                                {tr.back_to_website}
                            </Button>
                            <Button type="link" className="starter-button" onClick={startOnboarding}>
                                {tr.start_onboarding}
                            </Button>
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
}
